import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const reasonCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)

export const inventoryExceptionResolutionActionV1Schema = z.enum([
  "adjustment",
  "substitution",
  "transfer",
  "backdated_receipt",
])

export const inventoryExceptionResolveV1Schema = z
  .object({
    requestVersion: z.literal(1),
    correlationId: correlationIdSchema,
    idempotencyKey: z
      .string()
      .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    resolutionId: entityIdSchema,
    exceptionId: entityIdSchema,
    expectedVersion: z.literal(1),
    action: inventoryExceptionResolutionActionV1Schema,
    sourceLocationId: entityIdSchema.nullable(),
    substituteItemMasterId: entityIdSchema.nullable(),
    movementId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    postingDate: z.string().date(),
    evidenceReference: z.string().trim().min(3).max(256),
    reasonCode,
    billingDisposition: z.enum(["billable", "included", "non_billable"]),
    billingReferenceId: entityIdSchema.nullable(),
    billingTreatment: z.enum(["no_correction", "corrective_invoice_required"]),
    billingCorrectionId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const transfer = value.action === "transfer"
    const substitution = value.action === "substitution"
    const corrective = value.billingTreatment === "corrective_invoice_required"
    const billable = value.billingDisposition === "billable"
    if (
      transfer !== (value.sourceLocationId !== null) ||
      substitution !== (value.substituteItemMasterId !== null) ||
      billable !== (value.billingReferenceId !== null) ||
      corrective !== (value.billingCorrectionId !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Resolution references do not match the selected action.",
      })
    }
  })

export const inventoryExceptionResolutionResultV1Schema = z
  .object({
    entityType: z.literal("inventory_exception_resolution"),
    entityId: entityIdSchema,
    state: z.literal("Resolved"),
    version: z.literal(1),
    exceptionId: entityIdSchema,
    action: inventoryExceptionResolutionActionV1Schema,
    originalEventDate: z.string().date(),
    postingDate: z.string().date(),
    periodTreatment: z.enum([
      "original_period",
      "current_period_adjustment",
    ]),
    closedPeriodId: entityIdSchema.nullable(),
    supplyStockEntry: z.string().nullable(),
    consumptionMovementId: entityIdSchema,
    consumedItemMasterId: entityIdSchema,
    quantityMilli: z.number().int().positive(),
    valuationAmountMinor: z.number().int().nonnegative(),
    visitCostImpactMinor: z.number().int().nonnegative(),
    billingTreatment: z.enum(["no_correction", "corrective_invoice_required"]),
    billingCorrectionId: entityIdSchema.nullable(),
    exceptionStatus: z.literal("Resolved"),
  })
  .strict()

export type InventoryExceptionResolveV1 = z.infer<
  typeof inventoryExceptionResolveV1Schema
>
export type InventoryExceptionResolutionResultV1 = z.infer<
  typeof inventoryExceptionResolutionResultV1Schema
>
